import { GetMultiScoreResultsArgs, MultiScoreResults, MultiScoreTimeviewResults } from "types";
import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { cacheKeyForDateRange, defaultChartQueryOptions } from "utils";

import { MultiScoresAPI } from "api";

const queryKeys = {
  all: () => ["multi-scores"] as const,
  detail: (id: string | number) => [queryKeys.all(), id] as const,
  results: ({ startDate, endDate, multiScoreId: id, ...restArgs }: GetMultiScoreResultsArgs) => [
    ...queryKeys.detail(id),
    {
      ...restArgs,
      ...cacheKeyForDateRange({ startDate, endDate }),
    },
  ],
  timeviewResults: (args: GetMultiScoreResultsArgs) => [...queryKeys.results(args), "timeview"],
};

/**
 * Provides a query to fetch MultiScoreResults data.
 * This is extracted so that MultiScoreTimeview and MultiScoreSegment can share
 * the same query as the MultiScoreOverview component.
 */
export const useMultiScoreResults = <T = MultiScoreResults>(
  args: GetMultiScoreResultsArgs,
  options: UseQueryOptions<MultiScoreResults, unknown, T> = {},
) =>
  useQuery<MultiScoreResults, unknown, T>(queryKeys.results(args), () => MultiScoresAPI.getResults(args), {
    ...defaultChartQueryOptions,
    ...options,
  });

/**
 * Fetches multi score timeview results.
 */
export const useMultiScoreTimeviewResults = <T = MultiScoreTimeviewResults>(
  args: GetMultiScoreResultsArgs,
  options: UseQueryOptions<MultiScoreTimeviewResults, unknown, T> = {},
) =>
  useQuery<MultiScoreTimeviewResults, unknown, T>(
    queryKeys.timeviewResults(args),
    () => MultiScoresAPI.getTimeview(args),
    {
      ...defaultChartQueryOptions,
      ...options,
    },
  );
